import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/Admin/AdminLayout';
import { fetchWithSecurity, sanitizePayload } from '../../utils/security';
import { toast } from 'react-toastify';
import { Tag, Plus, RefreshCw, Trash2, ArrowLeft, Layers, Edit2, Check, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

const AdminBlogCategories = () => {
    const { t, i18n } = useTranslation();
    const { user, isSuperAdmin, isBlogManager, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({
        name_ka: '',
        name_en: '',
        slug: '',
        description: ''
    });
    const [editingId, setEditingId] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    const isKa = i18n.language === 'ka';

    useEffect(() => {
        if (!authLoading && !(isSuperAdmin() || isBlogManager())) navigate('/admin');
    }, [authLoading, isSuperAdmin, isBlogManager, navigate]);

    const fetchCategories = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetchWithSecurity(`${API_URL}categories.php?type=blog`);
            const data = await response.json();
            setCategories(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error fetching blog categories:', error);
            toast.error(t('common.toastLoadError') || 'კატეგორიების ჩატვირთვა ვერ მოხერხდა');
        }
        setLoading(false);
    }, [t]);

    useEffect(() => {
        if (user) fetchCategories();
    }, [user, fetchCategories]);

    const handleSlugify = (name) => {
        return name
            .toLowerCase()
            .trim()
            .replace(/[^\w\s-]/g, '')
            .replace(/[\s_-]+/g, '-')
            .replace(/^-+|-+$/g, '');
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const updated = { ...prev, [name]: value };
            if (name === 'name_en' && !editingId && !prev.slug) {
                updated.slug = handleSlugify(value);
            }
            return updated;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name_ka.trim() || !formData.slug.trim()) {
            toast.error('გთხოვთ მიუთითოთ კატეგორიის სახელი და სლაგი');
            return;
        }

        setIsSaving(true);
        try {
            const payload = sanitizePayload({
                ...formData,
                id: editingId || undefined
            });

            const response = await fetchWithSecurity(`${API_URL}categories.php?type=blog`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const result = await response.json();
            if (result.success) {
                toast.success(editingId ? 'კატეგორია განახლდა' : 'კატეგორია დაემატა');
                setFormData({ name_ka: '', name_en: '', slug: '', description: '' });
                setEditingId(null);
                fetchCategories();
            } else {
                toast.error(result.error || 'შენახვა ვერ მოხერხდა');
            }
        } catch (error) {
            toast.error('ოპერაცია ვერ შესრულდა');
        }
        setIsSaving(false);
    };

    const handleEdit = (cat) => {
        setEditingId(cat.id);
        setFormData({
            name_ka: cat.name_ka || '',
            name_en: cat.name_en || '',
            slug: cat.slug || '',
            description: cat.description || ''
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setFormData({ name_ka: '', name_en: '', slug: '', description: '' });
    };

    const handleDelete = async (id, name) => {
        if (!window.confirm(`დარწმუნებული ხართ, რომ გსურთ წაშალოთ კატეგორია "${name}"?`)) return;

        try {
            const response = await fetchWithSecurity(`${API_URL}categories.php?type=blog&id=${id}`, {
                method: 'DELETE'
            });
            const result = await response.json();
            if (result.success) {
                toast.success('კატეგორია წაიშალა');
                fetchCategories();
            } else {
                toast.error(result.error || 'წაშლა ვერ მოხერხდა');
            }
        } catch (error) {
            toast.error('წაშლა ვერ მოხერხდა');
        }
    };

    if (authLoading || !user) return null;

    return (
        <AdminLayout title={isKa ? 'ბლოგის კატეგორიები' : 'Blog Categories'}>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <button className="shadcn-btn shadcn-btn-secondary" onClick={() => navigate('/admin/blogs')}>
                    <ArrowLeft size={16} /> {isKa ? 'სტატიებში დაბრუნება' : 'Back to Blogs'}
                </button>
                <div className="d-flex gap-2">
                    <button className="shadcn-btn shadcn-btn-secondary" onClick={fetchCategories}>
                        <RefreshCw size={15} /> {isKa ? 'განახლება' : 'Refresh'}
                    </button>
                </div>
            </div>

            <div className="row g-4">
                {/* Form Card */}
                <div className="col-lg-4">
                    <div className="shadcn-card sticky-top" style={{ top: '90px' }}>
                        <div className="shadcn-card-header">
                            <div className="shadcn-card-title d-flex align-items-center gap-2">
                                <Tag size={18} className="text-primary" />
                                <span>{editingId ? (isKa ? 'კატეგორიის რედაქტირება' : 'Edit Category') : (isKa ? 'ახალი კატეგორიის დამატება' : 'Add New Category')}</span>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="p-3">
                            <div className="mb-3">
                                <label className="form-group-label">
                                    🇬🇪 {isKa ? 'სახელი (ქართულად)' : 'Name (Georgian)'} *
                                </label>
                                <input
                                    type="text"
                                    name="name_ka"
                                    value={formData.name_ka}
                                    onChange={handleInputChange}
                                    className="shadcn-input"
                                    placeholder="მაგ. ტექნოლოგიები"
                                    required
                                />
                            </div>

                            <div className="mb-3">
                                <label className="form-group-label">
                                    🇬🇧 {isKa ? 'სახელი (ინგლისურად)' : 'Name (English)'}
                                </label>
                                <input
                                    type="text"
                                    name="name_en"
                                    value={formData.name_en}
                                    onChange={handleInputChange}
                                    className="shadcn-input"
                                    placeholder="e.g. Technology"
                                />
                            </div>

                            <div className="mb-3">
                                <label className="form-group-label">
                                    {isKa ? 'სლაგი (იდენტიფიკატორი URL-სთვის)' : 'Slug (URL Identifier)'} *
                                </label>
                                <input
                                    type="text"
                                    name="slug"
                                    value={formData.slug}
                                    onChange={handleInputChange}
                                    className="shadcn-input"
                                    placeholder="tech"
                                    required
                                />
                            </div>

                            <div className="mb-4">
                                <label className="form-group-label">
                                    {isKa ? 'აღწერა (არასავალდებულო)' : 'Description (Optional)'}
                                </label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    className="shadcn-input"
                                    rows="2"
                                    placeholder={isKa ? 'მოკლე აღწერა...' : 'Short description...'}
                                />
                            </div>

                            <div className="d-flex gap-2">
                                <button type="submit" className="shadcn-btn shadcn-btn-primary w-100" disabled={isSaving}>
                                    {editingId ? <Check size={16} /> : <Plus size={16} />}
                                    {isSaving ? '...' : (editingId ? (isKa ? 'განახლება' : 'Update') : (isKa ? 'დამატება' : 'Add Category'))}
                                </button>
                                {editingId && (
                                    <button type="button" className="shadcn-btn shadcn-btn-secondary" onClick={handleCancelEdit}>
                                        <X size={16} />
                                    </button>
                                )}
                            </div>
                        </form>
                    </div>
                </div>

                {/* List Card */}
                <div className="col-lg-8">
                    <div className="shadcn-card">
                        <div className="shadcn-card-header d-flex justify-content-between align-items-center">
                            <div className="shadcn-card-title d-flex align-items-center gap-2">
                                <Layers size={18} className="text-primary" />
                                <span>{isKa ? 'არსებული კატეგორიები' : 'Existing Categories'} ({categories.length})</span>
                            </div>
                        </div>

                        {loading ? (
                            <div className="text-center py-5 text-muted">იტვირთება...</div>
                        ) : (
                            <div className="shadcn-table-wrapper">
                                <table className="shadcn-table">
                                    <thead>
                                        <tr>
                                            <th>ID</th>
                                            <th>🇬🇪 {isKa ? 'ქართული' : 'Georgian'}</th>
                                            <th>🇬🇧 {isKa ? 'ინგლისური' : 'English'}</th>
                                            <th>{isKa ? 'სლაგი' : 'Slug'}</th>
                                            <th className="text-end">{isKa ? 'მოქმედებები' : 'Actions'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {categories.map((cat) => (
                                            <tr key={cat.id} style={{ background: editingId === cat.id ? 'rgba(79, 70, 229, 0.05)' : 'transparent' }}>
                                                <td className="text-muted fw-bold">#{cat.id}</td>
                                                <td className="fw-bold">{cat.name_ka}</td>
                                                <td className="text-muted">{cat.name_en || '—'}</td>
                                                <td>
                                                    <span className="shadcn-badge shadcn-badge-secondary font-monospace">
                                                        {cat.slug}
                                                    </span>
                                                </td>
                                                <td className="text-end">
                                                    <div className="d-flex justify-content-end gap-2">
                                                        <button
                                                            className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm"
                                                            onClick={() => handleEdit(cat)}
                                                            title={isKa ? 'რედაქტირება' : 'Edit'}
                                                        >
                                                            <Edit2 size={13} />
                                                        </button>
                                                        <button
                                                            className="shadcn-btn shadcn-btn-danger shadcn-btn-sm"
                                                            onClick={() => handleDelete(cat.id, cat.name_ka)}
                                                            title={isKa ? 'წაშლა' : 'Delete'}
                                                        >
                                                            <Trash2 size={13} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        {categories.length === 0 && (
                                            <tr>
                                                <td colSpan="5" className="text-center py-4 text-muted">
                                                    {isKa ? 'კატეგორიები არ მოიძებნა' : 'No categories found'}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
};

export default AdminBlogCategories;
